import * as mediasoupClient from 'mediasoup-client'
import { SFU_CONFIG, getWsUrl } from '../config'
import { createDebugLogger } from '../../config/debugLog'

const sfuDebug = createDebugLogger('sfu')

export class SfuClient {
  constructor({ conversationId, peerId }) {
    this.conversationId = String(conversationId || '')
    this.peerId = String(peerId || '')
    this.ws = null
    this.device = null
    this.sendTransport = null
    this.recvTransport = null
    this.producers = []
    this.consumers = []
    this.videoProducer = null
    this.audioProducer = null
    this._handlers = new Map()
    this._subscribedProducers = new Set()
    this._producerStreams = new Map() // producerId -> MediaStream
    this._recvQueue = Promise.resolve()
    this._connectPromise = null
    this._sendTransportPromise = null
    this._recvTransportPromise = null
    this._transportCreateQueue = Promise.resolve()
    this._consumerByProducer = new Map() // producerId -> consumerId
  }

  on(type, cb) {
    const list = this._handlers.get(type) || []
    list.push(cb)
    this._handlers.set(type, list)
  }
  _emit(type, payload) {
    const list = this._handlers.get(type) || []
    list.forEach((cb) => {
      try {
        cb(payload)
      } catch {}
    })
  }

  async connect() {
    if (this._connectPromise) return this._connectPromise
    const url = typeof getWsUrl === 'function' ? getWsUrl() : SFU_CONFIG.wsUrl
    const token = await SFU_CONFIG.authToken()
    this._connectPromise = new Promise((resolve, reject) => {
      let settled = false
      try {
        const ws = new WebSocket(url)
        this.ws = ws
        ws.onopen = () => {
          sfuDebug('[SFU] ws open', url)
          ws.send(
            JSON.stringify({
              action: 'join',
              data: { conversationId: this.conversationId, peerId: this.peerId, token }
            })
          )
        }
        ws.onmessage = async (ev) => {
          const msg = JSON.parse(ev.data)
          if (msg.action === 'joined') {
            sfuDebug('[SFU] joined')
            await this._setupDevice(msg.data.rtpCapabilities)
            try {
              sfuDebug('[SFU] device loaded', { canProduce: this.device?.canProduce('video') })
            } catch {}
            this._emit('joined', null)
            if (!settled) {
              settled = true
              resolve()
            }
          } else if (msg.action === 'transportCreated') {
            sfuDebug('[SFU] transportCreated')
            this._onTransportCreated(msg.data)
          } else if (msg.action === 'transportConnected') {
            sfuDebug('[SFU] transportConnected')
            this._emit('transportConnected', msg.data)
          } else if (msg.action === 'produced') {
            sfuDebug('[SFU] produced')
            this._emit('produced', msg.data)
          } else if (msg.action === 'newProducer') {
            sfuDebug('[SFU] newProducer', msg.data)
            this._emit('newProducer', msg.data)
          } else if (msg.action === 'consuming') {
            sfuDebug('[SFU] consuming')
            this._emit('consuming', msg.data)
          } else if (msg.action === 'consumerResumed') {
            this._emit('consumerResumed', msg.data)
          } else if (msg.action === 'peerList') {
            sfuDebug('[SFU] peerList', msg.data)
            this._emit('peerList', msg.data)
          } else if (msg.action === 'peerJoined') {
            sfuDebug('[SFU] peerJoined', msg.data)
            this._emit('peerJoined', msg.data)
          } else if (msg.action === 'peerLeft') {
            sfuDebug('[SFU] peerLeft', msg.data)
            this._emit('peerLeft', msg.data)
          } else if (msg.action === 'error') {
            console.warn('[SFU] error', msg.data?.message)
            if (!settled) {
              settled = true
              reject(new Error(msg.data?.message || 'sfu-error'))
            }
          }
        }
        ws.onerror = (e) => {
          console.error('[SFU] ws error', e?.message || e)
          if (!settled) {
            settled = true
            reject(e?.message || 'ws-error')
          }
        }
        ws.onclose = () => {
          sfuDebug('[SFU] ws close')
          this._emit('closed')
          if (!settled) {
            settled = true
            reject('ws-closed')
          }
        }
      } catch (e) {
        reject(e)
      }
    })
    try {
      await this._connectPromise
      return this._connectPromise
    } catch (e) {
      this._connectPromise = null
      throw e
    }
  }

  send(action, data) {
    this.ws?.send?.(JSON.stringify({ action, data }))
  }

  async _setupDevice(routerRtpCapabilities) {
    this.device = new mediasoupClient.Device()
    await this.device.load({ routerRtpCapabilities })
  }

  async _ensureDeviceLoaded() {
    if (this.device?.loaded) return
    if (this._connectPromise) {
      try {
        await this._connectPromise
      } catch {}
    }
    if (!this.device?.loaded) {
      throw new Error('device-not-loaded')
    }
  }

  _enqueueTransportCreate(task) {
    const run = this._transportCreateQueue.then(task, task)
    this._transportCreateQueue = run.then(
      () => undefined,
      () => undefined
    )
    return run
  }

  async createSendTransport() {
    if (this.sendTransport) return this.sendTransport
    if (this._sendTransportPromise) return this._sendTransportPromise
    this._sendTransportPromise = this._enqueueTransportCreate(async () => {
      await this._ensureDeviceLoaded()
      this.send('createTransport', {})
      const data = await this._waitFor('transportCreated', { timeoutMs: 10000 })
      const transport = this.device.createSendTransport(data)
      transport.on('connect', ({ dtlsParameters }, cb) => {
        sfuDebug('[SFU] sendTransport.connect')
        this.send('connectTransport', { transportId: data.id, dtlsParameters })
        this._waitFor('transportConnected', {
          predicate: (payload) => payload?.transportId === data.id,
          timeoutMs: 10000
        })
          .then(() => cb())
          .catch(() => cb())
      })
      transport.on('produce', ({ kind, rtpParameters }, cb) => {
        sfuDebug('[SFU] sendTransport.produce', { kind })
        this.send('produce', { transportId: data.id, kind, rtpParameters })
        this._waitFor('produced', { timeoutMs: 10000 })
          .then(({ producerId }) => cb({ id: producerId }))
          .catch(() => cb({ id: undefined }))
      })
      this.sendTransport = transport
      return transport
    })
    try {
      return await this._sendTransportPromise
    } finally {
      this._sendTransportPromise = null
    }
  }

  async createRecvTransport() {
    if (this.recvTransport) return this.recvTransport
    if (this._recvTransportPromise) return this._recvTransportPromise
    this._recvTransportPromise = this._enqueueTransportCreate(async () => {
      await this._ensureDeviceLoaded()
      this.send('createTransport', {})
      const data = await this._waitFor('transportCreated', { timeoutMs: 10000 })
      const transport = this.device.createRecvTransport(data)
      transport.on('connect', ({ dtlsParameters }, cb) => {
        this.send('connectTransport', { transportId: data.id, dtlsParameters })
        this._waitFor('transportConnected', {
          predicate: (payload) => payload?.transportId === data.id,
          timeoutMs: 10000
        })
          .then(() => cb())
          .catch(() => cb())
      })
      this.recvTransport = transport
      return transport
    })
    try {
      return await this._recvTransportPromise
    } finally {
      this._recvTransportPromise = null
    }
  }

  async publish(stream) {
    await this._ensureDeviceLoaded()
    if (!this.sendTransport) await this.createSendTransport()
    const v = stream.getVideoTracks?.()[0]
    const a = stream.getAudioTracks?.()[0]
    try {
      sfuDebug('[SFU] publish()', {
        video: v ? { enabled: v.enabled, readyState: v.readyState } : null,
        audio: a ? { enabled: a.enabled, readyState: a.readyState } : null
      })
    } catch {}
    if (v)
      this.videoProducer = await this.sendTransport.produce({
        track: v,
        encodings: [{ maxBitrate: 1000000, scalabilityMode: 'L1T3' }]
      })
    if (this.videoProducer) this.producers.push(this.videoProducer)
    if (a) {
      this.audioProducer = await this.sendTransport.produce({
        track: a,
        codecOptions: {
          opusDtx: false,
          opusFec: true,
          opusStereo: false,
          opusMaxAverageBitrate: 64000,
          opusMaxPlaybackRate: 48000,
          opusPtime: 20
        }
      })
      this.producers.push(this.audioProducer)
    }
  }

  async replaceVideoTrack(track) {
    try {
      if (!track) return
      sfuDebug('[SFU] replaceVideoTrack called')
      if (this.videoProducer && typeof this.videoProducer.replaceTrack === 'function') {
        await this.videoProducer.replaceTrack({ track })
        sfuDebug('[SFU] replaceVideoTrack done')
      }
    } catch (e) {
      console.warn('[SFU] replaceVideoTrack failed', e?.message || e)
    }
  }

  async subscribe(producerId, opts = {}) {
    // Serialize recv negotiations to avoid SDP race (duplicate MID)
    const runSubscribe = async () => {
      const pid = String(producerId || '')
      if (!pid) return null
      // Guard against duplicate subscriptions for same producer
      if (this._subscribedProducers.has(pid)) {
        const cached = this._producerStreams.get(pid)
        if (cached) return cached
      }
      await this._ensureDeviceLoaded()
      if (!this.recvTransport) await this.createRecvTransport()
      const payload = {
        transportId: this.recvTransport.id,
        producerId: pid,
        rtpCapabilities: this.device.rtpCapabilities
      }
      try {
        if (opts && typeof opts === 'object') {
          if (typeof opts.paused === 'boolean') payload.paused = !!opts.paused
          if (opts.preferredLayers && typeof opts.preferredLayers === 'object') {
            const { spatialLayer, temporalLayer } = opts.preferredLayers
            const pl = {}
            if (typeof spatialLayer === 'number' && spatialLayer >= 0)
              pl.spatialLayer = spatialLayer
            if (typeof temporalLayer === 'number' && temporalLayer >= 0)
              pl.temporalLayer = temporalLayer
            if (Object.keys(pl).length) payload.preferredLayers = pl
          }
        }
      } catch {}
      this.send('consume', payload)
      const { id, kind, rtpParameters } = await this._waitFor('consuming', {
        predicate: (payload) => String(payload?.producerId || '') === pid,
        timeoutMs: 10000
      })
      const consumer = await this.recvTransport.consume({
        id,
        producerId: pid,
        kind,
        rtpParameters
      })
      this.consumers.push(consumer)
      try {
        this._consumerByProducer.set(pid, id)
      } catch {}

      const encLen = Array.isArray(rtpParameters?.encodings) ? rtpParameters.encodings.length : 0
      sfuDebug('[SFU] subscribe consumer', {
        producerId: pid,
        consumerId: id,
        kind,
        encodings: encLen,
        trackState: consumer.track?.readyState,
        trackEnabled: consumer.track?.enabled
      })

      if (consumer.track) {
        consumer.track.enabled = true
      }

      // Resume consumer - required for receiving data
      try {
        if (!opts || opts.paused !== true) {
          await consumer.resume()
          sfuDebug('[SFU] consumer resumed', { consumerId: id, kind })
        }
      } catch (e) {
        console.warn('[SFU] consumer.resume() failed', e?.message)
      }

      // Tell server to resume (belt and suspenders)
      if (!opts || opts.paused !== true) this.send('resumeConsumer', { consumerId: id })

      if (kind === 'video') {
        try {
          if (typeof consumer.setPreferredLayers === 'function') {
            if (encLen > 1) {
              const spatialLayer = Math.max(0, encLen - 1)
              await consumer.setPreferredLayers({ spatialLayer, temporalLayer: 2 })
            } else {
              await consumer.setPreferredLayers({ temporalLayer: 2 })
            }
          }
        } catch (e) {
          console.warn('[SFU] setPreferredLayers failed', e?.message)
        }
        try {
          if (typeof consumer.requestKeyFrame === 'function') {
            setTimeout(() => {
              try {
                consumer.requestKeyFrame?.()
              } catch {}
            }, 0)
            setTimeout(() => {
              try {
                consumer.requestKeyFrame?.()
              } catch {}
            }, 500)
          }
        } catch (e) {
          console.warn('[SFU] requestKeyFrame failed', e?.message)
        }
      }

      const stream = consumer.track ? new MediaStream([consumer.track]) : new MediaStream()
      try {
        sfuDebug('[SFU] subscribe created stream', {
          producerId: pid,
          consumerId: id,
          kind,
          encodings: encLen,
          trackKind: consumer.track?.kind,
          trackState: consumer.track?.readyState,
          enabled: consumer.track?.enabled
        })
      } catch {}
      this._subscribedProducers.add(pid)
      this._producerStreams.set(pid, stream)
      return stream
    }
    this._recvQueue = this._recvQueue.then(runSubscribe, runSubscribe)
    return this._recvQueue
  }

  async pauseConsumer(consumerId) {
    try {
      const c = this.consumers.find((x) => x.id === consumerId)
      if (c && typeof c.pause === 'function') await c.pause()
    } catch {}
    try {
      this.send('pauseConsumer', { consumerId })
    } catch {}
  }

  async resumeConsumer(consumerId) {
    try {
      const c = this.consumers.find((x) => x.id === consumerId)
      if (c && typeof c.resume === 'function') await c.resume()
    } catch {}
    try {
      this.send('resumeConsumer', { consumerId })
    } catch {}
  }

  _waitFor(type, { predicate, timeoutMs = 15000 } = {}) {
    return new Promise((resolve, reject) => {
      let timer = null
      const cleanup = () => {
        const list = this._handlers.get(type) || []
        const idx = list.indexOf(handler)
        if (idx >= 0) list.splice(idx, 1)
        this._handlers.set(type, list)
        if (timer) {
          clearTimeout(timer)
          timer = null
        }
      }
      const handler = (data) => {
        if (typeof predicate === 'function') {
          let ok = false
          try {
            ok = !!predicate(data)
          } catch {}
          if (!ok) return
        }
        cleanup()
        resolve(data)
      }
      this.on(type, handler)
      if (timeoutMs > 0) {
        timer = setTimeout(() => {
          cleanup()
          reject(new Error(`[SFU] timeout waiting for ${type}`))
        }, timeoutMs)
      }
    })
  }

  _onTransportCreated(data) {
    this._emit('transportCreated', data)
  }

  close() {
    try {
      this.consumers.forEach((c) => c.close())
    } catch {}
    try {
      this.producers.forEach((p) => p.close())
    } catch {}
    try {
      this.sendTransport?.close()
    } catch {}
    try {
      this.recvTransport?.close()
    } catch {}
    try {
      this.ws?.close()
    } catch {}
  }

  // Live control helpers
  async setPreferredLayers(consumerId, layers = {}) {
    const { spatialLayer, temporalLayer } = layers || {}
    try {
      const c = this.consumers.find((x) => x.id === consumerId)
      if (c && typeof c.setPreferredLayers === 'function') {
        await c.setPreferredLayers({
          spatialLayer:
            typeof spatialLayer === 'number' && spatialLayer >= 0 ? spatialLayer : undefined,
          temporalLayer:
            typeof temporalLayer === 'number' && temporalLayer >= 0 ? temporalLayer : undefined
        })
      }
    } catch {}
    try {
      this.send('setPreferredLayers', { consumerId, spatialLayer, temporalLayer })
    } catch {}
  }

  async setPreferredLayersForProducer(producerId, layers = {}) {
    try {
      const pid = String(producerId || '')
      if (!pid) return
      const consumerId = this._consumerByProducer.get(pid)
      if (!consumerId) return
      await this.setPreferredLayers(consumerId, layers)
    } catch {}
  }

  async pauseForProducer(producerId) {
    try {
      const pid = String(producerId || '')
      if (!pid) return
      const consumerId = this._consumerByProducer.get(pid)
      if (!consumerId) return
      await this.pauseConsumer(consumerId)
    } catch {}
  }

  async resumeForProducer(producerId) {
    try {
      const pid = String(producerId || '')
      if (!pid) return
      const consumerId = this._consumerByProducer.get(pid)
      if (!consumerId) return
      await this.resumeConsumer(consumerId)
    } catch {}
  }
}

export default { SfuClient }
